import type { BunkerPhase } from './bunker-phases';

export const BUNKER_MIN_PLAYERS = 4;
export const BUNKER_RECOMMENDED_MIN_PLAYERS = 6;
export const BUNKER_RECOMMENDED_MAX_PLAYERS = 16;
export const BUNKER_MAX_PLAYERS = 24;

export type BunkerRoomStatus = 'lobby' | 'running' | 'finished' | 'cancelled';
export type BunkerConnectionStatus = 'not_joined' | 'connected' | 'disconnected' | 'spectator';
export type BunkerContentRating = 'family' | 'standard';
export type BunkerGameMode = 'quick' | 'standard';
export type BunkerFieldStatus =
  | 'hidden'
  | 'available_to_reveal'
  | 'selected_for_reveal'
  | 'revealed'
  | 'blocked'
  | 'changed'
  | 'stolen'
  | 'discarded'
  | 'falsified';

export type BunkerCharacterFieldType =
  | 'profession'
  | 'health'
  | 'hobby'
  | 'baggage'
  | 'fact1'
  | 'fact2'
  | 'phobia'
  | 'trait';

export const BUNKER_FIELD_LABEL: Record<BunkerCharacterFieldType, string> = {
  profession: 'Профессия',
  health: 'Здоровье',
  hobby: 'Хобби',
  baggage: 'Багаж',
  fact1: 'Факт №1',
  fact2: 'Факт №2',
  phobia: 'Фобия',
  trait: 'Черта характера',
};

export interface BunkerGameSettings {
  mode: BunkerGameMode;
  contentRating: BunkerContentRating;
  /** `null` means floor(playerCount / 2) at game start. */
  capacity: number | null;
  maxPlayers: number;
  introductionSeconds: number;
  characterReviewSeconds: number;
  roundStartSeconds: number;
  revealSeconds: number;
  speechSeconds: number;
  discussionSeconds: number;
  actionWindowSeconds: number;
  votingSeconds: number;
  tieDiscussionSeconds: number;
  resultSeconds: number;
  specialCardsEnabled: boolean;
  randomEventsEnabled: boolean;
  survivalSimulationEnabled: boolean;
  selfVotingAllowed: boolean;
}

export const DEFAULT_BUNKER_SETTINGS: BunkerGameSettings = {
  mode: 'quick',
  contentRating: 'standard',
  capacity: null,
  maxPlayers: 16,
  introductionSeconds: 18,
  characterReviewSeconds: 45,
  roundStartSeconds: 5,
  revealSeconds: 30,
  speechSeconds: 60,
  discussionSeconds: 120,
  actionWindowSeconds: 20,
  votingSeconds: 45,
  tieDiscussionSeconds: 45,
  resultSeconds: 6,
  specialCardsEnabled: true,
  randomEventsEnabled: true,
  survivalSimulationEnabled: true,
  selfVotingAllowed: false,
};

export interface BunkerRoomSummary {
  roomId: string;
  code: string;
  state: BunkerRoomStatus;
  joinUrl: string;
  qrPayload: string;
  settings: BunkerGameSettings;
  phase: BunkerPhase;
  roundNumber: number;
  capacity: number | null;
  playerCount: number;
  activePlayerCount: number;
  stateVersion: number;
}

export interface BunkerPlayerPublic {
  playerId: string;
  name: string;
  avatarId: string;
  status: BunkerConnectionStatus;
  isCreator: boolean;
  isReady: boolean;
  isActive: boolean;
  isEliminated: boolean;
  eliminatedRound: number | null;
  revealedFields: BunkerRevealedField[];
  specialCardCount: number;
  hasImmunity: boolean;
}

export interface BunkerRevealedField {
  type: BunkerCharacterFieldType;
  label: string;
  value: string;
  detail: string | null;
  changed: boolean;
}

export interface BunkerCharacterField {
  type: BunkerCharacterFieldType;
  label: string;
  value: string;
  detail: string | null;
  tags: string[];
  score: number;
  status: BunkerFieldStatus;
  revealedAt: string | null;
  revealedRound: number | null;
  changed: boolean;
}

export interface BunkerCharacterPrivate {
  age: number;
  biology: string;
  fields: BunkerCharacterField[];
  balanceScore: number;
}

export interface BunkerScenarioPublic {
  id: string;
  title: string;
  shortDescription: string;
  fullDescription: string;
  isolation: string;
  survivalRate: string;
  tags: string[];
}

export interface BunkerInfoPublic {
  id: string;
  title: string;
  description: string;
  area: string;
  autonomy: string;
  revealedFeatures: string[];
  hiddenFeatureCount: number;
}

export type BunkerSpecialCardType =
  | 'immunity'
  | 'double_vote'
  | 'force_reveal'
  | 'reveal_bunker'
  | 'swap_baggage';

export interface BunkerSpecialCard {
  instanceId: string;
  type: BunkerSpecialCardType;
  title: string;
  description: string;
  timing: 'ACTION_WINDOW';
  priority: number;
  status: 'available' | 'used';
}

export interface BunkerLogEntry {
  id: string;
  at: string;
  text: string;
  visibility: 'public';
}

export interface BunkerVoteTotal {
  playerId: string;
  name: string;
  votes: number;
}

export interface BunkerSurvivalResult {
  score: number;
  outcome: 'success' | 'losses' | 'unstable' | 'failure' | 'fatal';
  title: string;
  summary: string;
  strengths: string[];
  risks: string[];
  story: string[];
}

export interface BunkerPrivateState {
  screen:
    | 'lobby'
    | 'intro'
    | 'character_review'
    | 'reveal_selection'
    | 'player_speech'
    | 'discussion'
    | 'action_window'
    | 'voting'
    | 'vote_results'
    | 'tie_discussion'
    | 'elimination'
    | 'results'
    | 'waiting';
  phase: BunkerPhase;
  stateVersion: number;
  viewerPlayerId: string;
  roundNumber: number;
  endsAt: string | null;
  isCreator: boolean;
  isActive: boolean;
  isEliminated: boolean;
  isReady: boolean;
  capacity: number | null;
  activePlayerCount: number;
  currentPlayerId: string | null;
  currentPlayerName: string | null;
  currentTurnIndex: number;
  currentTurnTotal: number;
  canSelectReveal: boolean;
  selectableFields: BunkerCharacterFieldType[];
  character: BunkerCharacterPrivate | null;
  specialCards: BunkerSpecialCard[];
  scenario: BunkerScenarioPublic | null;
  bunker: BunkerInfoPublic | null;
  players: BunkerPlayerPublic[];
  selectedVoteId: string | null;
  votingSubmittedCount: number;
  votingPlayerCount: number;
  voteCandidates: { playerId: string; name: string }[];
  voteTotals: BunkerVoteTotal[];
  tieCandidates: { playerId: string; name: string }[];
  eliminatedPlayer: { playerId: string; name: string } | null;
  logs: BunkerLogEntry[];
  survival: BunkerSurvivalResult | null;
  finalCharacters: Array<{ playerId: string; name: string; character: BunkerCharacterPrivate }>;
  paused: boolean;
}
